import { Box, Text, Textarea } from '@mantine/core';
import { useDebouncedCallback } from '@mantine/hooks';
import {
	AnalyticsEvent,
	AnalyticsEventType,
	AnalyticsJourney,
	AnalyticsStage,
	analyticsSdk
} from '@newo/analytics';
import { type FC, useCallback, useRef } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useHostContext } from '@/context/HostContext.tsx';
import {
	type StepperOnboardingFormValues,
	StepperSetupStepKey
} from '@/stepper/types.ts';
import { STEP_CONTENT_CLASS_NAMES } from '../../classNames';
import StepperNextButton from '../StepperNextButton';

interface StepperGreetingStepContentProps {
	isLocked?: boolean;
	onNext: () => void;
}

const StepperGreetingStepContent: FC<StepperGreetingStepContentProps> = ({
	isLocked = false,
	onNext
}) => {
	const { localization } = useHostContext();
	const { control } = useFormContext<StepperOnboardingFormValues>();
	const greetingPhrase = useWatch({
		control,
		name: 'greetingPhrase'
	});
	const previousGreetingPhraseRef = useRef(greetingPhrase);

	const debouncedGreetingPhraseChange = useDebouncedCallback(
		(nextGreetingPhrase: string) => {
			if (!nextGreetingPhrase.trim()) {
				return;
			}

			analyticsSdk.track(AnalyticsEvent.FieldChanged, {
				field: 'greetingPhrase',
				greeting_phrase: nextGreetingPhrase.trim(),
				journey: AnalyticsJourney.CreatorSetup,
				stage: AnalyticsStage.CreatorOnboarding,
				step: StepperSetupStepKey.CustomizeGreeting,
				type: AnalyticsEventType.Field
			});
		},
		600
	);

	const handleGreetingPhraseChange = useCallback(
		(nextGreetingPhrase: string) => {
			const previousGreetingPhrase = previousGreetingPhraseRef.current.trim();
			previousGreetingPhraseRef.current = nextGreetingPhrase;

			if (!nextGreetingPhrase.trim() && previousGreetingPhrase) {
				analyticsSdk.track(AnalyticsEvent.FieldCleared, {
					field: 'greetingPhrase',
					journey: AnalyticsJourney.CreatorSetup,
					previous_greeting_phrase: previousGreetingPhrase,
					stage: AnalyticsStage.CreatorOnboarding,
					step: StepperSetupStepKey.CustomizeGreeting,
					type: AnalyticsEventType.Field
				});
			}

			debouncedGreetingPhraseChange(nextGreetingPhrase);
		},
		[debouncedGreetingPhraseChange]
	);

	const handleGreetingFocus = useCallback(() => {
		analyticsSdk.track(AnalyticsEvent.FieldFocused, {
			field: 'greetingPhrase',
			journey: AnalyticsJourney.CreatorSetup,
			stage: AnalyticsStage.CreatorOnboarding,
			step: StepperSetupStepKey.CustomizeGreeting,
			type: AnalyticsEventType.Field
		});
	}, []);

	const handleNextClick = useCallback(() => {
		analyticsSdk.track(AnalyticsEvent.ButtonClicked, {
			action: 'next',
			field: 'greetingPhrase',
			greeting_phrase: greetingPhrase.trim(),
			journey: AnalyticsJourney.CreatorSetup,
			stage: AnalyticsStage.CreatorOnboarding,
			step: StepperSetupStepKey.CustomizeGreeting,
			type: AnalyticsEventType.Interaction
		});
		analyticsSdk.track(AnalyticsEvent.StepCompleted, {
			journey: AnalyticsJourney.CreatorSetup,
			stage: AnalyticsStage.CreatorOnboarding,
			step: StepperSetupStepKey.CustomizeGreeting,
			type: AnalyticsEventType.Step
		});
		onNext();
	}, [greetingPhrase, onNext]);

	return (
		<Box className={STEP_CONTENT_CLASS_NAMES.stepBody}>
			<Text className={STEP_CONTENT_CLASS_NAMES.stepSubtitle}>
				{localization.translate(
					'creator',
					'stepperOnboarding.steps.customizeGreeting.description'
				)}
			</Text>

			<Controller
				control={control}
				name="greetingPhrase"
				render={({ field }) => (
					<Textarea
						{...field}
						classNames={{
							input: STEP_CONTENT_CLASS_NAMES.textArea,
							label: STEP_CONTENT_CLASS_NAMES.formLabel,
							root: 'mt-[33px] w-full'
						}}
						data-testid="stepper-greeting-textarea"
						onChange={(event) => {
							handleGreetingPhraseChange(event.currentTarget.value);
							field.onChange(event);
						}}
						onFocus={handleGreetingFocus}
						readOnly={isLocked}
					/>
				)}
			/>

			<Box className="mt-4">
				<StepperNextButton
					disabled={isLocked}
					onClick={handleNextClick}
					testId="stepper-greeting-next-button"
				>
					{localization.translate(
						'creator',
						'stepperOnboarding.steps.customizeGreeting.next'
					)}
				</StepperNextButton>
			</Box>
		</Box>
	);
};

export default StepperGreetingStepContent;
